import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Message as MessageIcon,
  Send as SendIcon,
  Reply as ReplyIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Inbox as InboxIcon,
  Outbox as OutboxIcon,
  Drafts as DraftsIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { useSnackbar } from 'notistack';

const Messages = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [replyDialog, setReplyDialog] = useState(false);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get('student-portal/messages');
      if (response.data.success) {
        // Extract messages array from the response
        const messagesData = response.data.data.messages || [];
        setMessages(Array.isArray(messagesData) ? messagesData : []);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch messages', { variant: 'error' });
      setMessages([]); // Ensure messages is always an array
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`student-portal/messages/${messageId}/read`);
      setMessages(prev => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return prevArray.map(msg => 
          msg.message_id === messageId ? { ...msg, is_read: true } : msg
        );
      });
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) {
      enqueueSnackbar('Please enter a reply message', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('student-portal/send-message', {
        teacher_id: selectedMessage.sender_id,
        subject: `Re: ${selectedMessage.subject}`,
        message: replyText.trim(),
        parent_message_id: selectedMessage.message_id
      });

      if (response.data.success) {
        enqueueSnackbar('Reply sent successfully', { variant: 'success' });
        setReplyDialog(false);
        setReplyText('');
        fetchMessages();
      }
    } catch (error) {
      enqueueSnackbar('Failed to send reply', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      const response = await axios.delete(`student-portal/messages/${messageId}`);
      if (response.data.success) {
        enqueueSnackbar('Message deleted successfully', { variant: 'success' });
        setMessages(prev => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return prevArray.filter(msg => msg.message_id !== messageId);
        });
        setViewDialog(false);
      }
    } catch (error) {
      enqueueSnackbar('Failed to delete message', { variant: 'error' });
    }
  };

  const getFilteredMessages = () => {
    // Ensure messages is an array before filtering
    const messagesArray = Array.isArray(messages) ? messages : [];
    switch (tabValue) {
      case 0: // Inbox
        return messagesArray.filter(msg => msg.message_type === 'received');
      case 1: // Sent
        return messagesArray.filter(msg => msg.message_type === 'sent');
      case 2: // Unread
        return messagesArray.filter(msg => msg.message_type === 'received' && !msg.is_read);
      default:
        return messagesArray;
    }
  };

  const getMessageStats = () => {
    // Ensure messages is an array before filtering
    const messagesArray = Array.isArray(messages) ? messages : [];
    const received = messagesArray.filter(msg => msg.message_type === 'received');
    const sent = messagesArray.filter(msg => msg.message_type === 'sent');
    const unread = received.filter(msg => !msg.is_read);
    
    return { total: messagesArray.length, received: received.length, sent: sent.length, unread: unread.length };
  };

  const stats = getMessageStats();
  const filteredMessages = getFilteredMessages();

  const MessageCard = ({ message }) => (
    <motion.div
      whileHover={{ scale: 1.01 }}
      transition={{ duration: 0.2 }}
    >
      <Card 
        sx={{ 
          mb: 1, 
          cursor: 'pointer',
          border: message.is_read ? '1px solid #e0e0e0' : '2px solid #2196F3',
          bgcolor: message.is_read ? 'background.paper' : 'primary.light'
        }}
        onClick={() => {
          setSelectedMessage(message);
          setViewDialog(true);
          if (!message.is_read && message.message_type === 'received') {
            markAsRead(message.message_id);
          }
        }}
      >
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                {message.sender_name?.charAt(0) || message.recipient_name?.charAt(0)}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={message.is_read ? 'normal' : 'bold'}>
                  {message.message_type === 'received' ? message.sender_name : `To: ${message.recipient_name}`}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="textSecondary"
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {message.subject}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="textSecondary"
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block'
                  }}
                >
                  {message.message.substring(0, 100)}...
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Typography variant="caption" color="textSecondary">
                {new Date(message.created_at).toLocaleDateString()}
              </Typography>
              {!message.is_read && message.message_type === 'received' && (
                <Chip label="New" color="primary" size="small" sx={{ mt: 0.5 }} />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Messages
        </Typography>
        <IconButton onClick={fetchMessages} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #2196F320 0%, #2196F310 100%)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <InboxIcon sx={{ fontSize: 40, color: '#2196F3', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="#2196F3">
                {stats.received}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Received
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4CAF5020 0%, #4CAF5010 100%)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <OutboxIcon sx={{ fontSize: 40, color: '#4CAF50', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="#4CAF50">
                {stats.sent}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Sent
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #FF980020 0%, #FF980010 100%)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={stats.unread} color="error">
                <DraftsIcon sx={{ fontSize: 40, color: '#FF9800', mb: 1 }} />
              </Badge>
              <Typography variant="h4" fontWeight="bold" color="#FF9800">
                {stats.unread}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Unread
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #9C27B020 0%, #9C27B010 100%)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <MessageIcon sx={{ fontSize: 40, color: '#9C27B0', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="#9C27B0">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Message Tabs and List */}
      <Card>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab 
                label={
                  <Badge badgeContent={stats.received} color="primary" showZero>
                    Inbox
                  </Badge>
                } 
              />
              <Tab 
                label={
                  <Badge badgeContent={stats.sent} color="success" showZero>
                    Sent
                  </Badge>
                } 
              />
              <Tab 
                label={
                  <Badge badgeContent={stats.unread} color="error">
                    Unread
                  </Badge>
                } 
              />
            </Tabs>
          </Box>

          {filteredMessages.length > 0 ? (
            <Box>
              {filteredMessages.map((message) => (
                <MessageCard key={message.message_id} message={message} />
              ))}
            </Box>
          ) : (
            <Alert severity="info">
              {tabValue === 0 && 'No messages in your inbox'}
              {tabValue === 1 && 'No sent messages'}
              {tabValue === 2 && 'No unread messages'}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* View Message Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedMessage?.subject}
          </Typography>
          <IconButton onClick={() => setViewDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    {selectedMessage.sender_name?.charAt(0) || selectedMessage.recipient_name?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedMessage.message_type === 'received' 
                        ? `From: ${selectedMessage.sender_name}` 
                        : `To: ${selectedMessage.recipient_name}`
                      }
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {new Date(selectedMessage.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {selectedMessage.message}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => deleteMessage(selectedMessage?.message_id)}
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
          {selectedMessage?.message_type === 'received' && (
            <Button 
              onClick={() => {
                setReplyDialog(true);
                setViewDialog(false);
              }}
              variant="contained"
              startIcon={<ReplyIcon />}
            >
              Reply
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialog} onClose={() => setReplyDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Reply to: {selectedMessage?.subject}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Your Reply"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply here..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialog(false)}>Cancel</Button>
          <Button 
            onClick={sendReply}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={loading || !replyText.trim()}
          >
            {loading ? 'Sending...' : 'Send Reply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Messages;
